import React from 'react';
import { createTask as createTaskApi } from '../../api/tasks/client';
import type { TaskLink } from '../../api/tasks/interface';
import useArtifactsHook from '../../data/artifacts/useArtifactsHook';
import { useSelector } from '../../data/store';
import useTasksHook, {
  type CreateTaskInput,
} from '../../data/tasks/useTasksHook';
import useFilesHook from '../ClaimOverview/FilesListContainer/useFilesHook';
import { useEscDossierContext } from '../DossierContext';
import TasksPanel, { type TaskLinkOption } from './TasksPanel';

interface Props {
  dossierId: string;
}

const TasksTab: React.FC<Props> = ({ dossierId }) => {
  const [{ tasks, hasData, loading }, handler] = useTasksHook(dossierId);
  const { onTaskClick, onArtifactClick, onFileClick } = useEscDossierContext();

  const currentUser = useSelector((s) => s.session.current);

  const defaultAssignee =
    currentUser?.full_name?.trim() || currentUser?.email || 'You';

  const assigneeOptions = React.useMemo(
    () => [defaultAssignee],
    [defaultAssignee]
  );

  const [{ artifacts, hasData: hasArtifactData }, artifactsHandler] =
    useArtifactsHook(dossierId);
  const artFetchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasArtifactData || artFetchedRef.current === dossierId) {
      return;
    }
    artFetchedRef.current = dossierId;
    artifactsHandler.fetchArtifacts();
  }, [dossierId, hasArtifactData, artifactsHandler]);

  const [{ files: filesRecord }, filesHandler] = useFilesHook(dossierId);
  const hasFilesData = useSelector(
    (s) => s.files.files[dossierId] !== undefined
  );
  const filesFetchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasFilesData || filesFetchedRef.current === dossierId) {
      return;
    }
    filesFetchedRef.current = dossierId;
    filesHandler.fetchFiles({ stateDb: true });
  }, [dossierId, hasFilesData, filesHandler]);

  const linkOptions = React.useMemo<TaskLinkOption[]>(() => {
    const artOpts: TaskLinkOption[] = artifacts.map((a) => ({
      type: 'artifact',
      value: a.artifact_id,
      label: a.schema_name,
    }));
    const noteOpts: TaskLinkOption[] = Object.values(filesRecord)
      .filter((f) => f.file_type === 'ACTIVITY')
      .map((f) => ({
        type: 'note',
        value: f.file_id,
        label: f.data?.description || f.file_name,
      }));
    return [...artOpts, ...noteOpts];
  }, [artifacts, filesRecord]);

  const fetchedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId || hasData || fetchedRef.current === dossierId) {
      return;
    }
    fetchedRef.current = dossierId;
    handler.fetchTasks();
  }, [dossierId, hasData, handler]);

  // "Add with details": create immediately (so we have an id), insert into the
  // store, then open the task detail for the full editing surface.
  const onAddWithDetails = React.useCallback(
    async (input: CreateTaskInput) => {
      const task = await createTaskApi({ dossierId, ...input });
      handler.addTask(task);
      onTaskClick(task.id);
    },
    [dossierId, handler, onTaskClick]
  );

  const onOpenLink = React.useCallback(
    (link: TaskLink) => {
      if (link.type === 'task') {
        onTaskClick(link.id);
      } else if (link.type === 'artifact') {
        onArtifactClick(link.id);
      } else if (link.type === 'note') {
        onFileClick(link.id);
      }
    },
    [onTaskClick, onArtifactClick, onFileClick]
  );

  return (
    <TasksPanel
      tasks={tasks}
      handler={handler}
      loading={loading}
      assigneeOptions={assigneeOptions}
      defaultAssignee={defaultAssignee}
      linkOptions={linkOptions}
      onAddWithDetails={onAddWithDetails}
      onOpenLink={onOpenLink}
      onOpenDetail={onTaskClick}
    />
  );
};

export default TasksTab;
